'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useState } from 'react';
import { PRICING_PLANS, type PricingFeatureIconName, type PricingPlan, type PricingPlanId } from '@/entities/pricing';
import { DEFAULT_LANGUAGE } from '@/shared/localization';
import { RevealOnScroll } from '@/shared/ui/reveal-on-scroll';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import styles from './SecPricing.module.scss';
const COMMERCE_PRICING_ORIGIN = 'https://vaetki.ai';
type PricingButtonProps = {
  children: ReactNode;
  href: string;
};
function PricingButton(t0) {
  const $ = _c(7);
  const {
    children,
    href
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span aria-hidden="true" className={styles.pricingCtaOverlayPurple} />;
    t2 = <span aria-hidden="true" className={styles.pricingCtaOverlayGradient} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children) {
    t3 = <span className={styles.ctaLabel}>{children}</span>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== href || $[5] !== t3) {
    t4 = <a href={href} className={styles.pricingCta}>{t1}{t2}{t3}</a>;
    $[4] = href;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
const FEATURE_ICON_SRC: Record<PricingFeatureIconName, string> = {
  doc: '/intro/ic_pricing_pdp.svg',
  sparkle: '/intro/ic_pricing_ai.svg',
  chat: '/intro/ic_pricing_chatbot.svg',
  check: '/intro/ic_pricing_check.svg',
  lab: '/intro/ic_pricing_flask.svg'
};
function PricingFeatureIcon(t0) {
  const $ = _c(2);
  const {
    icon
  } = t0;
  const t1 = FEATURE_ICON_SRC[icon];
  let t2;
  if ($[0] !== t1) {
    t2 = <img src={t1} alt="" className={styles.featureIcon} aria-hidden="true" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}
function BookmarkIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg width="8" height="10" viewBox="0 0 8 10" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.bookmarkIcon} aria-hidden="true"><path d="M0 0H8V10L4 7.5L0 10V0Z" fill="currentColor" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
type PricingCardProps = {
  plan: PricingPlan;
  startUrl: string;
  isActive: boolean;
  isDimmed: boolean;
  onHover: () => void;
  onLeave: () => void;
};
function PricingCard(t0) {
  const $ = _c(54);
  const {
    plan,
    startUrl,
    isActive,
    isDimmed,
    onHover,
    onLeave
  } = t0;
  const t = useTranslations("pricing");
  const isEnterprise = plan.id === "enterprise";
  let t1;
  if ($[0] !== isEnterprise || $[1] !== plan.features) {
    t1 = isEnterprise ? plan.features.slice(0, 1) : plan.features;
    $[0] = isEnterprise;
    $[1] = plan.features;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const featureItems = t1;
  let t2;
  if ($[3] !== plan.id || $[4] !== t) {
    t2 = t.raw(`plans.${plan.id}.features`);
    $[3] = plan.id;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const featureLabels = t2 as string[];
  const t3 = `plans.${plan.id}.heading`;
  let t4;
  if ($[6] !== t || $[7] !== t3) {
    t4 = t(t3);
    $[6] = t;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const heading = t4;
  const t5 = `plans.${plan.id}.credits`;
  let t6;
  if ($[9] !== t || $[10] !== t5) {
    t6 = t(t5);
    $[9] = t;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const credits = t6;
  const t7 = `plans.${plan.id}.buttonLabel`;
  let t8;
  if ($[12] !== t || $[13] !== t7) {
    t8 = t(t7);
    $[12] = t;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const buttonLabel = t8;
  const t9 = isActive ? styles.cardActive : "";
  const t10 = isDimmed ? styles.cardDimmed : "";
  let t11;
  if ($[15] !== t10 || $[16] !== t9) {
    t11 = [styles.card, t9, t10].filter(Boolean);
    $[15] = t10;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const cardClassName = t11.join(" ");
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div aria-hidden="true" className={styles.cardSurface} />;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== plan.eyebrow) {
    t13 = <h3 className={styles.cardTitle}>{plan.eyebrow}</h3>;
    $[19] = plan.eyebrow;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== plan.badge) {
    t14 = plan.badge ? <span className={styles.badge}><BookmarkIcon /><span className={styles.badgeLabel}>{plan.badge}</span></span> : null;
    $[21] = plan.badge;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t13 || $[24] !== t14) {
    t15 = <div className={styles.cardHeader}>{t13}{t14}</div>;
    $[23] = t13;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== heading || $[27] !== plan.price || $[28] !== plan.priceSuffix) {
    t16 = plan.price ? <div className={styles.priceRow}><p className={styles.priceLine}><span className={styles.price}>{plan.price}</span><span className={styles.priceSpacer}> </span><span className={styles.priceSuffix}>{plan.priceSuffix}</span></p></div> : <div className={styles.headingText}>{heading}</div>;
    $[26] = heading;
    $[27] = plan.price;
    $[28] = plan.priceSuffix;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const t17 = isEnterprise ? "mailto:vaetki-commerce@ncsoft.com" : startUrl;
  let t18;
  if ($[30] !== buttonLabel || $[31] !== t17) {
    t18 = <PricingButton href={t17}>{buttonLabel}</PricingButton>;
    $[30] = buttonLabel;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== credits) {
    t19 = credits ? <div className={styles.creditsRow}>{credits}</div> : null;
    $[33] = credits;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  const t20 = isEnterprise ? `${styles.featuresList} ${styles.featuresListExpanded}` : styles.featuresList;
  let t21;
  if ($[35] !== featureItems || $[36] !== featureLabels) {
    let t22;
    if ($[38] !== featureLabels) {
      t22 = (feature, index) => <div key={featureLabels[index]} className={styles.featureRow}><div className={styles.featureIconWrap}><PricingFeatureIcon icon={feature.icon} /></div><p className={styles.featureLabel}>{featureLabels[index]}</p></div>;
      $[38] = featureLabels;
      $[39] = t22;
    } else {
      t22 = $[39];
    }
    t21 = featureItems.map(t22);
    $[35] = featureItems;
    $[36] = featureLabels;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[40] !== t20 || $[41] !== t21) {
    t22 = <div className={t20}>{t21}</div>;
    $[40] = t20;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== t15 || $[44] !== t16 || $[45] !== t18 || $[46] !== t19 || $[47] !== t22) {
    t23 = <div className={styles.cardInner}>{t15}{t16}{t18}{t19}{t22}</div>;
    $[43] = t15;
    $[44] = t16;
    $[45] = t18;
    $[46] = t19;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== cardClassName || $[50] !== onHover || $[51] !== onLeave || $[52] !== t23) {
    t24 = <article onMouseEnter={onHover} onMouseLeave={onLeave} className={cardClassName}>{t12}{t23}</article>;
    $[49] = cardClassName;
    $[50] = onHover;
    $[51] = onLeave;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  return t24;
}
type LocaleParams = {
  locale: string;
};
export function SecPricing() {
  const $ = _c(13);
  const t = useTranslations("pricing");
  const params = useParams();
  const locale = params?.locale ?? DEFAULT_LANGUAGE;
  const startUrl = `${COMMERCE_PRICING_ORIGIN}/${locale}/pricing`;
  const [hoveredPlan, setHoveredPlan] = useState(null);
  let t0;
  if ($[0] !== t) {
    t0 = t("tagline");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <RevealOnScroll delayMs={80} y={18}><div className={styles.headingBlock}><h2 className={styles.tagline}>{t0}</h2></div></RevealOnScroll>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <RevealOnScroll delayMs={1200} durationMs={980} y={104} className={styles.bgImageWrap}><img src="/intro/pricing_bg-point.svg" alt="" aria-hidden="true" className={styles.bgImage} /></RevealOnScroll>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== hoveredPlan || $[6] !== startUrl) {
    t3 = PRICING_PLANS.map((plan, index) => <RevealOnScroll key={plan.id} delayMs={220 + index * 90} durationMs={1200} y={28} className={styles.cardSlot} transitionProperties="opacity, transform"><PricingCard plan={plan} startUrl={startUrl} isActive={hoveredPlan === plan.id} isDimmed={hoveredPlan !== null && hoveredPlan !== plan.id} onHover={() => setHoveredPlan(plan.id)} onLeave={() => setHoveredPlan(null)} /></RevealOnScroll>);
    $[5] = hoveredPlan;
    $[6] = startUrl;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <div className={styles.cardsArea}>{t2}<div className={styles.cardsRow}>{t3}</div></div>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t1 || $[11] !== t4) {
    t5 = <section id="pricing" data-section="pricing" className={styles.section}><div className={styles.outer}>{t1}{t4}</div></section>;
    $[10] = t1;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}